import '../../styles/restScreens.css';
import '../../styles/platformTiles.css';
import { EMPTY_ACTIVITY, NO_MESSAGES_TEXT, NO_OPEN_ITEMS_TEXT, NO_TRIPS_TEXT } from '../../lib/currentActivity';
import { toneTextStyle } from './clientsConstants';

// The right half of a Hybrid client card - "Recent activity" (Val, Oct 25 2026: "critical contact info with recent activities
// preview"). A short timeline: the next (or last) trip, the latest quote, the last message, the latest invoice and what is still open.
// Pure display of the model built by src/lib/currentActivity.js (which only ever holds what the viewer may see) plus the account's own
// latest invoice (loaded with the client list, only for viewers with financial access). Empty states keep their exact words:
// "No trips yet", "No messages yet" and "No open items".
const heading = 'text-[11px] uppercase tracking-wide text-[var(--ink-muted)]';

function TripLine({ trip }) {
    const when = [trip.dateLabel, trip.timeLabel].filter(Boolean).join(' · ') || (trip.askedLabel ? `asked ${trip.askedLabel}` : '');
    return (
        <div data-testid={`activity-${trip.kind}`}>
            <div className="text-sm font-medium" style={toneTextStyle(trip.kind === 'quote' ? 'system' : trip.kind === 'next' ? 'good' : 'muted')}>
                {trip.label}{trip.amountLabel ? ` · ${trip.amountLabel}` : ''}
            </div>
            {when && <div className="text-sm text-[var(--ink)]">{when}</div>}
            {trip.route && <div className="truncate text-sm text-[var(--ink-muted)]" title={trip.route}>{trip.route}</div>}
        </div>
    );
}

export default function CurrentActivityPeek({ activity = EMPTY_ACTIVITY, loading = false, recentInvoice = null }) {
    const model = activity || EMPTY_ACTIVITY;
    const invoice = recentInvoice && recentInvoice.kind === 'invoice' ? recentInvoice : null;
    return (
        <section data-m6-layout="clients" data-m6-component="CurrentActivityPeek" aria-label="Recent activity" data-testid="current-activity" className="ia-hybrid-peek min-w-0 bg-[var(--surface-sunken)] p-4">
            <div className={heading}>Recent activity</div>
            <ol className="ia-recent-list">
                <li className="ia-recent-item" data-testid="activity-trips">
                    {model.trip ? <TripLine trip={model.trip} />
                        : <div className="text-sm italic text-[var(--ink-muted)]">{loading ? 'Loading…' : NO_TRIPS_TEXT}</div>}
                    {model.quote && <div className="mt-2"><TripLine trip={model.quote} /></div>}
                </li>

                <li className="ia-recent-item" data-testid="activity-message">
                    <div className="ia-recent-item__label">Last message</div>
                    {model.lastMessage ? (
                        <div className="text-sm text-[var(--ink)]">
                            {model.lastMessage.channelLabel} {model.lastMessage.direction === 'inbound' ? 'from the client' : 'sent'}
                            <span className="text-[var(--ink-muted)]"> · {model.lastMessage.atLabel}</span>
                        </div>
                    ) : <div className="text-sm italic text-[var(--ink-muted)]">{loading ? 'Loading…' : NO_MESSAGES_TEXT}</div>}
                </li>

                {invoice && (
                    <li className="ia-recent-item" data-testid="activity-invoice">
                        <div className="ia-recent-item__label">Latest invoice</div>
                        <div className="text-sm font-medium" style={toneTextStyle(invoice.tone)}>{invoice.label}</div>
                        <div className="text-sm text-[var(--ink)]">{invoice.detail}{invoice.date ? <span className="text-[var(--ink-muted)]"> · {invoice.date}</span> : null}</div>
                    </li>
                )}

                <li className="ia-recent-item" data-testid="activity-open-items">
                    <div className="ia-recent-item__label">Open items</div>
                    {model.openItems.length ? (
                        <ul className="mt-0.5 space-y-0.5 text-sm">
                            {model.openItems.map((item) => <li key={item} style={toneTextStyle('open')}>{item}</li>)}
                            {model.openItemsHidden > 0 && <li className="text-[var(--ink-muted)]">+{model.openItemsHidden} more</li>}
                        </ul>
                    ) : <div className="text-sm italic text-[var(--ink-muted)]">{loading ? 'Loading…' : NO_OPEN_ITEMS_TEXT}</div>}
                </li>
            </ol>
        </section>
    );
}
